<template>
  <custom-dialog
    icon="mdi-information-variant"
    color="info"
    :title="property ? `${property.code} : ${property.name}` : ''"
    @close="handleCloseClick"
    boilerplate
    v-resize="onResize"
  >
    <template v-slot:actions>
      <alert-actions :isMenu="false" hasLabel :alert="item" />
    </template>
    <div class="d-flex">
      <div
        class="d-flex mr-2 elevation-5"
        :style="`width: ${sidePanelIsOpen ? '16%' : '34px'};`"
      >
        <div v-show="sidePanelIsOpen" style="width: 100%">
          <alerts-left-side :height="height" />
        </div>
        <div :style="{ height: height + 'px' }" class="d-flex align-center">
          <div class="primary menu-bar" />
          <v-btn
            style="position: relative; left: 11px; z-index: 10"
            @click="handleToggleLeftPanel"
            icon
            class="primary"
            small
          >
            <v-icon color="background">
              {{ sidePanelIsOpen ? 'navigate_before' : 'navigate_next' }}
            </v-icon>
          </v-btn>
        </div>
      </div>
      <div :style="`width: ${sidePanelIsOpen ? 84 : 100}%;`">
        <v-tabs
          v-model="tab"
          :background-color="$vuetify.theme.themes[theme].background"
          :height="TABS_HEIGHT"
        >
          <v-tab>
            {{ generateMessage('requests.details.titles.property') }}
          </v-tab>
          <v-tab v-show="beginSensors || hasSensors">
            <v-badge color="error" v-if="hasUnseenErrors">
              <span slot="badge">!</span>
              {{ generateMessage('requests.details.titles.sondes') }}
            </v-badge>
            <div v-else>
              {{ generateMessage('requests.details.titles.sondes') }}
            </div>
          </v-tab>
          <v-tab>{{ generateMessage('requests.details.titles.fri') }}</v-tab>
          <v-tab>
            {{ generateMessage('requests.details.titles.logbook') }}
          </v-tab>
          <v-tab>
            {{ generateMessage('requests.details.titles.documents') }}
          </v-tab>
        </v-tabs>
        <v-tabs-items
          v-model="tab"
          :style="{
            height: height + 'px',
            width: '100%',
            background: $vuetify.theme.themes[theme].background,
            'overflow-y': 'auto !important',
          }"
          class="overflow-y-hidden"
        >
          <v-tab-item>
            <loader :is-loading="isLoading">
              <property-general />
              <div class="d-flex flex-wrap">
                <v-col cols="12" md="6">
                  <property-contract />
                </v-col>
                <v-col cols="12" md="6">
                  <property-exploitation />
                </v-col>
              </div>
            </loader>
          </v-tab-item>
          <v-tab-item>
            <div
              class="d-flex flex-nowrap"
              :style="{ height: height - 20 + 'px' }"
            >
              <v-col cols="12" xl="6">
                <sensors-table
                  :height="height - 170"
                  class="ma-2"
                  style="width: 100%"
                />
              </v-col>
              <v-col cols="12" xl="6">
                <sensors-errors-table
                  :height="height - 170"
                  class="ma-2"
                  style="width: 100%"
                />
              </v-col>
            </div>
          </v-tab-item>
          <v-tab-item>
            <summary-property-sheet />
          </v-tab-item>
          <v-tab-item>
            <logbook-reduced-filters
              :style="{
                height: '80px',
              }"
            />
            <div
              :style="{
                height: height - 80 + 'px',
                'overflow-y': 'auto',
              }"
            >
              <logbook />
            </div>
          </v-tab-item>
          <v-tab-item>
            <documents-horizontal-filters
              :style="{
                height: '80px',
              }"
            />
            <div
              :style="{
                height: height - 80 + 'px',
                'overflow-y': 'auto',
              }"
            >
              <property-documents is-modal />
            </div>
          </v-tab-item>
        </v-tabs-items>
      </div>
    </div>
  </custom-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import { generateMessage } from '@/utils/i18n';
import CustomDialog from '@/components/dialogs/CustomDialog';
import Loader from '@/components/widgets/Loader';
import resizable from '@/business/mixins/resizable';
import {
  POPUP_TOP_HEIGHT,
  POPUP_BOTTOM_HEIGHT,
  POPUP_MARGINS,
} from '@/components/dialogs/customDialog.styles';
import { TABS_HEIGHT } from '@/pages/eDispatch/requestDetails/rightSide/requestsRightSide';
import PropertyExploitation from '@/pages/eDispatch/requestDetails/rightSide/propertyTab/PropertyExploitation';
import PropertyContract from '@/pages/eDispatch/requestDetails/rightSide/propertyTab/PropertyContract';
import PropertyGeneral from '@/pages/eDispatch/requestDetails/rightSide/propertyTab/PropertyGeneral';
import SummaryPropertySheet from '@/pages/FRI/SummaryPropertySheet';
import PropertyDocuments from '@/pages/property/documents/PropertyDocuments';
import DocumentsHorizontalFilters from '@/pages/property/documents/DocumentsHorizontalFilters';
import Logbook from '@/pages/property/logbook/Logbook';
import LogbookReducedFilters from '@/pages/property/logbook/LogbookReducedFilters';
import AlertActions from '@/pages/hypervision/AlertActions';
import SensorsErrorsTable from './SensorsErrorsTable';
import SensorsTable from './SensorsTable';
import AlertsLeftSide from './AlertsLeftSide';

export default {
  name: 'PropertyDialog',
  mixins: [resizable],
  components: {
    CustomDialog,
    SensorsTable,
    SensorsErrorsTable,
    PropertyGeneral,
    PropertyContract,
    PropertyExploitation,
    SummaryPropertySheet,
    Logbook,
    LogbookReducedFilters,
    PropertyDocuments,
    DocumentsHorizontalFilters,
    AlertActions,
    AlertsLeftSide,
    Loader,
  },
  computed: {
    ...mapGetters({
      property: 'properties_360/current',
      isLoading: 'properties_360/isLoadingCurrent',
      modal: 'boilerplate/isModalOpen',
      item: 'alerts/current',
      sensors: 'sensors/items',
      errors: 'sensors/errors/items',
    }),
    height() {
      return (
        this.boilerplatePopupHeight -
        TABS_HEIGHT -
        POPUP_TOP_HEIGHT -
        POPUP_BOTTOM_HEIGHT -
        POPUP_MARGINS
      );
    },
    beginSensors() {
      return this.$store.getters['boilerplate/modalInitialTab'] === 'sensors';
    },
    theme() {
      return this.$vuetify.theme.dark ? 'dark' : 'light';
    },
  },
  data() {
    return {
      TABS_HEIGHT,
      tab: () => {
        if (this.$store.getters['boilerplate/modalInitialTab'] === 'sensors') {
          return 1;
        }
        return 0;
      },
      errorHeaders: [
        { text: 'Libelle sonde', value: 'label' },
        { text: 'Problème', value: 'error_label' },
        { text: 'Date', value: 'error_datetime' },
        { text: 'Valeur', value: 'value' },
      ],
      sensorsHeaders: [
        { text: 'Type', value: 'type' },
        { text: 'Libelle', value: 'label' },
        { text: 'Unité mesure', value: 'unit' },
        { text: 'Nombre de décimales mesure', value: 'digit_number' },
      ],
      hasUnseenErrors: false,
      sidePanelIsOpen: true,
      hasSensors: false,
    };
  },
  methods: {
    generateMessage,
    handleToggleLeftPanel() {
      this.sidePanelIsOpen = !this.sidePanelIsOpen;
    },
    handleCloseClick() {
      this.$store.dispatch('sensors/properties/remove', this.property.id);
    },
  },
  watch: {
    modal(value) {
      if (value) {
        this.tab = () => {
          if (
            this.$store.getters['boilerplate/modalInitialTab'] === 'sensors'
          ) {
            return 1;
          }
          return 0;
        };
      }
    },
    errors(list) {
      this.hasUnseenErrors = this.tab !== 1 && list.length !== 0;
    },
    sensors(list) {
      this.hasSensors = list.length !== 0;
    },
    tab(val) {
      if (val === 1) {
        this.hasUnseenErrors = false;
      }
    },
  },
};
</script>

<style scoped>
.menu-bar {
  position: relative;
  width: 6px;
  height: 50%;
  left: 29px;
}
</style>
